import React from 'react';
import * as Sentry from '@sentry/react';
import { createBrowserRouter, createRoutesFromElements, RouterProvider, Route, Outlet } from 'react-router-dom';
import LandingPage from './components/LandingPage';
import PricingPage from './components/PricingPage';
import FAQPage from './components/FAQPage';
import AboutUsPage from './components/AboutUsPage';
import ContactPage from './components/ContactPage';
import LoginPage from './components/LoginPage';
import ForgotPasswordPage from './components/ForgotPasswordPage';
import ResetPasswordPage from './components/ResetPasswordPage';
import UpdatePasswordPage from './components/UpdatePasswordPage';
import SignupPage from './components/SignupPage';
import DashboardPage from './components/DashboardPage';
import NewClientPage from './components/NewClientPage';
import NewMedicalRecordPage from './components/NewMedicalRecordPage';
import PetMedicalHistoryPage from './components/PetMedicalHistoryPage';
import ClientsPage from './components/ClientsPage';
import EmailVerification from './components/EmailVerification';
import SettingsPage from './components/SettingsPage';
import EmployeesPage from './components/EmployeesPage';
import ShiftsPage from './components/ShiftsPage';
import NewAppointmentPage from './components/NewAppointmentPage';
import PublicBookingPage from './components/PublicBookingPage';
import ProtectedRoute from './components/ProtectedRoute';
import TermsPage from './components/TermsPage';
import ScrollToTop from './components/ScrollToTop';
import PrivacyPage from './components/PrivacyPage';
import EditMedicalRecordPage from './components/EditMedicalRecordPage';
import PetRemindersPage from './components/PetRemindersPage';
import TodoPage from './components/TodoPage';
import MessagesPage from './components/MessagesPage';
import WarehousePage from './components/WarehousePage';
import PendingApprovalsPage from './components/PendingApprovalsPage';
import SubscriptionPage from './components/SubscriptionPage';
import { AuthProvider } from './contexts/AuthContext';
import { UnreadMessagesProvider } from './contexts/UnreadMessagesContext';
import { WarehouseProvider } from './contexts/WarehouseContext';
import { PendingApprovalsProvider } from './contexts/PendingApprovalsContext';
import { TermsAcceptanceProvider } from './contexts/TermsAcceptanceContext';
import { OnboardingProvider } from './contexts/OnboardingContext';
import TermsAcceptanceModal from './components/TermsAcceptanceModal';
import OnboardingTourOverlay from './components/onboarding/OnboardingTourOverlay';
import NotFoundPage from './components/NotFoundPage';
import AuthRedirectHandler from './components/AuthRedirectHandler';

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

function AppLayout() {
  return (
    <AuthProvider>
      <OnboardingProvider>
        <WarehouseProvider>
          <UnreadMessagesProvider>
            <PendingApprovalsProvider>
              <TermsAcceptanceProvider>
                <AuthRedirectHandler />
                <ScrollToTop />
                <Outlet />
                <OnboardingTourOverlay />
                <TermsAcceptanceModal />
              </TermsAcceptanceProvider>
            </PendingApprovalsProvider>
          </UnreadMessagesProvider>
        </WarehouseProvider>
      </OnboardingProvider>
    </AuthProvider>
  );
}

const router = sentryCreateBrowserRouter(
  createRoutesFromElements(
    <Route element={<AppLayout />}>
      <Route path="/" element={<LandingPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/about" element={<AboutUsPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/update-password" element={<UpdatePasswordPage />} />
      <Route path="/book/:slug" element={<PublicBookingPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/new-client" element={<ProtectedRoute><NewClientPage /></ProtectedRoute>} />
      <Route path="/new-medical-record" element={<ProtectedRoute><NewMedicalRecordPage /></ProtectedRoute>} />
      <Route path="/new-medical-record/:petId" element={<ProtectedRoute><NewMedicalRecordPage /></ProtectedRoute>} />
      <Route path="/pet-medical-history/:petId" element={<ProtectedRoute><PetMedicalHistoryPage /></ProtectedRoute>} />
      <Route path="/medical-record/:recordId/edit" element={<ProtectedRoute><EditMedicalRecordPage /></ProtectedRoute>} />
      <Route path="/pet-reminders/:petId" element={<ProtectedRoute><PetRemindersPage /></ProtectedRoute>} />
      <Route path="/clients" element={<ProtectedRoute><ClientsPage /></ProtectedRoute>} />
      <Route path="/employees" element={<ProtectedRoute><EmployeesPage /></ProtectedRoute>} />
      <Route path="/shifts" element={<ProtectedRoute><ShiftsPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/warehouse" element={<ProtectedRoute><WarehousePage /></ProtectedRoute>} />
      <Route path="/todo" element={<ProtectedRoute><TodoPage /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
      <Route path="/new-appointment" element={<ProtectedRoute><NewAppointmentPage /></ProtectedRoute>} />
      <Route path="/pending-approvals" element={<ProtectedRoute><PendingApprovalsPage /></ProtectedRoute>} />
      <Route path="/subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
      <Route path="/email-verification" element={<EmailVerification />} />
      <Route path="/*" element={<NotFoundPage />} />
    </Route>
  )
);

function App() {
  return <RouterProvider router={router} />;
}

export default App;
